/**
 * Позиция, посчитанная по НАШИМ филлам.
 *
 * Чистый модуль без React и без сети по правилу lib/*.ts: здесь решается, какое
 * число оператор видит на месте «позиция N share @ цена», и это обязано
 * проверяться тестом, а не разглядыванием компонента.
 *
 * # Почему это ТОЧНЫЙ счёт, а не оптимистичная догадка
 *
 * Позиция в системе берётся у Polymarket Data API, который индексирует блокчейн и
 * отстаёт от факта исполнения на секунды (lib/position.ts). Для мейкер-торговли на
 * пятиминутном окне этого мало: оператор жмёт, ордер стоит в стакане, филл
 * приезжает по сокету за миллисекунды — а строка позиции ещё несколько секунд
 * говорит «позиции нет».
 *
 * Здесь она считается из наших же сделок, и на рынке `*-updown-*` это не
 * приближение: окно начинается с НУЛЕВОЙ позиции, значит наши филлы полностью её
 * определяют. Ошибиться можно только пропустив филл, а не сложив неполную историю
 * с неизвестным началом.
 *
 * # Что именно складывается
 *
 * `our_size` и `our_price`, а НЕ `price`/`size` сделки. Последние описывают
 * тейкер-ордер целиком: когда мейкером были мы, там стоят чужой размер и чужая
 * цена, зачастую по противоположному токену — наши 8.21 share по 0.73 записаны
 * строкой «173.39 @ 0.29» (docs/db-schema.md).
 *
 * # Почему по КАРТЕ, а не инкрементом
 *
 * Одна сделка приезжает несколько раз, шагая по статусам MATCHED -> MINED ->
 * CONFIRMED под одним id, и это обычный случай, а не редкость. Инкремент дал бы
 * тройную позицию. Поэтому вход здесь — набор сделок, а не поток событий, и
 * складывать его можно сколько угодно раз с одним результатом.
 */

import { add, cmp, div, mul, parse, sub, type Micro } from "./money";
import type { Side, Token, Trade } from "../api/types";

/** Позиция по одному токену, посчитанная по нашим филлам. */
export interface FilledPosition {
  /** Сколько share набрано. Отрицательное означает нетто-продажу. */
  shares: Micro;
  /**
   * Средняя цена ПОКУПОК, взвешенная по размеру. Ноль — покупок не было.
   *
   * Считается только по покупкам, а не по всем филлам: продажа не меняет цену
   * входа, а включённая в среднее — превращает её в бессмысленную величину между
   * ценой входа и ценой выхода. Индексатор считает так же (avg_price).
   */
  avgPrice: Micro;
  /** Сколько филлов сложено. Ноль — сделок по токену не было. */
  fills: number;
  /** Метка последнего учтённого филла, миллисекунды. Ноль — филлов не было. */
  lastAt: number;
  /**
   * Метка последней НАШЕЙ продажи, миллисекунды; ноль — продаж не было. По ней
   * снимок индексатора после продажи признаётся отставшим (journalAfterSale).
   */
  lastSellAt: number;
}

/**
 * Статусы, в которых сделка НЕ считается.
 *
 * FAILED и RETRYING — это сделка, которая on-chain не осела: считать её позицией
 * значило бы показать share, которых нет на балансе. Ровно то направление
 * осторожности, что у lib/position.ts, где резерв вычитается, а покупки не
 * прибавляются.
 */
const NOT_SETTLED = new Set(["FAILED", "RETRYING"]);

/**
 * Складывает наши сделки в позиции по токенам.
 *
 * Вход — сделки ОДНОГО рынка; фильтровать по рынку обязан вызывающий, потому что
 * стор ленты общий, а строка позиции всегда про открытый рынок.
 *
 * Сделки не наши (`ours !== true`) отбрасываются здесь же, а не у вызывающего:
 * публичный принт не несёт ни our_size, ни адреса, и сложить его с нашими
 * значило бы приписать себе весь оборот рынка.
 */
export function positionsFromFills(
  trades: readonly Trade[],
  tokens?: readonly Token[],
): Map<string, FilledPosition> {
  const out = new Map<string, FilledPosition>();

  for (const t of trades) {
    if (!t.ours) continue;
    if (t.status && NOT_SETTLED.has(t.status)) continue;

    const assetId = ourAsset(t, tokens);
    if (!assetId) continue;

    const size = parse(t.our_size);
    const price = parse(t.our_price);
    // Ноль в our_size означает «строк ещё не приезжало», а не «ноль»: ранний
    // статус приходит без maker_orders. Такую сделку складывать нечем.
    if (size === null || size <= 0) continue;

    const side = ourSide(t);
    const cur =
      out.get(assetId) ??
      ({ shares: 0, avgPrice: 0, fills: 0, lastAt: 0, lastSellAt: 0 } as FilledPosition);

    // Взвешенная средняя считается через СУММУ ЗАТРАТ, а не пересчётом средней:
    // цепочка «средняя от средней» на десятке филлов копит ошибку округления, а
    // сумма затрат — это ровно те деньги, что ушли.
    if (side === "BUY" && price !== null && price > 0) {
      const spentBefore = mul(cur.avgPrice, cur.shares > 0 ? cur.shares : 0);
      const spentNow = mul(price, size);
      const boughtBefore = cur.shares > 0 ? cur.shares : 0;
      const bought = add(boughtBefore, size);
      if (spentBefore !== null && spentNow !== null && bought !== null && bought > 0) {
        const spent = add(spentBefore, spentNow);
        const avg = spent === null ? null : div(spent, bought);
        if (avg !== null) cur.avgPrice = avg;
      }
    }

    const shares = side === "BUY" ? add(cur.shares, size) : sub(cur.shares, size);
    if (shares !== null) cur.shares = shares;
    cur.fills += 1;

    const at = Date.parse(t.timestamp ?? "");
    if (!Number.isNaN(at) && at > cur.lastAt) cur.lastAt = at;
    if (side === "SELL" && !Number.isNaN(at) && at > cur.lastSellAt) cur.lastSellAt = at;

    out.set(assetId, cur);
  }
  return out;
}

/**
 * НАШ токен в сделке.
 *
 * `trade.asset_id` — токен ТЕЙКЕР-ордера, и в мейкерской сделке это ЧУЖОЙ исход:
 * покупка NO по 0.73 сводится с продажей YES, и запись сделки описывает первую.
 * Взять его как свой — значит записать позицию по UP в DOWN, то есть получить
 * верный размер на неверном исходе. Ошибка тем опаснее, что выглядит правдоподобно:
 * число share настоящее.
 *
 * Порядок источников:
 *
 *  1. НАША строка филла — единственный прямой ответ, есть почти всегда;
 *  2. дополнение до второго токена рынка, когда мы мейкер, а строки филла нет
 *     (ранние статусы приезжают без maker_orders). Рынок бинарный, исходов ровно
 *     два, поэтому «не тейкерский» означает «второй»;
 *  3. токен сделки — тейкерский случай, где он и есть наш.
 *
 * Пустая строка означает «неизвестно»: сложить такую сделку некуда, и приписать её
 * наугад нельзя.
 */
export function ourAsset(t: Trade, tokens?: readonly Token[]): string {
  const own = t.fills?.find((f) => f.ours && f.asset_id);
  if (own?.asset_id) return own.asset_id;

  if (t.trader_side === "MAKER") {
    if (!tokens || tokens.length !== 2) return "";
    const other = tokens.find((tk) => tk.asset_id !== t.asset_id);
    return other?.asset_id ?? "";
  }
  return t.asset_id ?? "";
}

/**
 * Наша сторона в сделке.
 *
 * `side` описывает ТЕЙКЕР-ордер, поэтому при `trader_side === "MAKER"` наша
 * сторона ОБРАТНАЯ: чужая покупка закрывается нашей продажей. Прочитать side как
 * свой — значит получить позицию с верным размером и неверным знаком, то есть
 * ровно наоборот.
 *
 * У нашей строки филла сторона указана прямо, и она надёжнее вывода по роли:
 * берём её, когда есть.
 */
export function ourSide(t: Trade): Side {
  const own = t.fills?.find((f) => f.ours);
  if (own?.side) return own.side;
  if (t.trader_side === "MAKER") return t.side === "BUY" ? "SELL" : "BUY";
  return t.side;
}

/** Позиция по токену или null, если филлов по нему не было. */
export function filledPositionOf(
  positions: Map<string, FilledPosition>,
  assetId: string,
): FilledPosition | null {
  const p = positions.get(assetId);
  if (!p || p.fills === 0) return null;
  return p;
}

/**
 * Разошлись ли наш счёт и снимок индексатора, и на сколько.
 *
 * Возвращает разницу «наши филлы минус индексатор» в share. Ноль означает согласие,
 * положительное — индексатор отстал, отрицательное — он видит больше нашего (мы
 * пропустили филл либо позиция набрана не через нас).
 *
 * Отдельной функцией, потому что показывать это надо СЛОВАМИ, а не прятать: два
 * числа на экране без объяснения разницы читаются как ошибка интерфейса.
 */
export function indexerLag(ours: Micro, indexed: string | null | undefined): Micro | null {
  const idx = parse(indexed);
  if (idx === null) return null;
  return sub(ours, idx);
}

/**
 * Стоит ли говорить о расхождении.
 *
 * Порог — шаг размера ордера (0.01 share): индексатор отдаёт размеры до шести
 * знаков, и хвост в микро-долях означает не отставание, а точность.
 */
export function lagWorthShowing(lag: Micro | null): boolean {
  return lag !== null && cmp(Math.abs(lag), 10_000) >= 0;
}
